import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { Card, Btn, Field, Input, Select, Th, Badge, fmtN, useMasters, MasterOptions } from "./camCommon";

const EMPTY = {
  side: "buy", commodity: "", counterparty: "", volume: "", price: "",
  price_type: "fixed", price_formula: "", currency: "USD", location: "",
  delivery_start: "", delivery_end: "", transport_mode: "", status: "open",
};

export default function CamDeals() {
  const { byKind } = useMasters();
  const [deals, setDeals] = useState([]);
  const [fStatus, setFStatus] = useState("");
  const [fCommodity, setFCommodity] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (fStatus) p.set("status", fStatus);
    if (fCommodity) p.set("commodity", fCommodity);
    api.get(`/cam/deals?${p}`).then((r) => setDeals(r.data || [])).catch(() => {});
  }, [fStatus, fCommodity]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/cam/deals", form);
      toast.success(form.id ? "Deal updated" : "Deal created");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete deal ${d.deal_no}?`)) return;
    try {
      await api.delete(`/cam/deals/${d.id}`);
      toast.success("Deal deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="cam-deals">
      <Card title="Trade Capture" subtitle="Physical buy/sell deals for crude, refined products & biofuels — fixed or formula priced"
        actions={
          <div className="flex items-center gap-2">
            <Select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="!w-36" data-testid="deal-filter-status">
              <option value="">All statuses</option>
              {["open", "scheduled", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Select value={fCommodity} onChange={(e) => setFCommodity(e.target.value)} className="!w-40" data-testid="deal-filter-commodity">
              <option value="">All commodities</option>
              {byKind("commodity").map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </Select>
            <Btn onClick={() => setForm({ ...EMPTY })} data-testid="deal-new-btn">
              <Plus size={13} className="inline mr-1" /> New Deal
            </Btn>
          </div>
        } testid="cam-deals-card">

        {form && (
          <div className="mb-5 border border-[#C8A24B]/40 bg-amber-50/30 rounded-sm p-4" data-testid="deal-form">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#0B1E3F]">
                {form.id ? `Edit deal ${form.deal_no}` : "New deal"}
              </div>
              <button onClick={() => setForm(null)} data-testid="deal-form-close" className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Side">
                <Select value={form.side} onChange={set("side")} data-testid="deal-side">
                  <option value="buy">Buy</option><option value="sell">Sell</option>
                </Select>
              </Field>
              <Field label="Commodity *">
                <Select value={form.commodity} onChange={set("commodity")} data-testid="deal-commodity">
                  <MasterOptions items={byKind("commodity")} />
                </Select>
              </Field>
              <Field label="Counterparty *">
                <Select value={form.counterparty} onChange={set("counterparty")} data-testid="deal-counterparty">
                  <MasterOptions items={byKind("counterparty")} />
                </Select>
              </Field>
              <Field label="Location *">
                <Select value={form.location} onChange={set("location")} data-testid="deal-location">
                  <MasterOptions items={byKind("location")} />
                </Select>
              </Field>
              <Field label="Volume *">
                <Input type="number" min="0" value={form.volume} onChange={set("volume")} placeholder="e.g. 50000" data-testid="deal-volume" />
              </Field>
              <Field label="Price Type">
                <Select value={form.price_type} onChange={set("price_type")} data-testid="deal-price-type">
                  <option value="fixed">Fixed</option><option value="formula">Formula</option>
                </Select>
              </Field>
              {form.price_type === "fixed" ? (
                <Field label="Price *">
                  <Input type="number" min="0" step="0.01" value={form.price} onChange={set("price")} placeholder="e.g. 78.40" data-testid="deal-price" />
                </Field>
              ) : (
                <Field label="Price Formula">
                  <Input value={form.price_formula} onChange={set("price_formula")} placeholder="e.g. NYMEX RBOB avg + 0.45" data-testid="deal-formula" />
                </Field>
              )}
              <Field label="Currency">
                <Select value={form.currency} onChange={set("currency")} data-testid="deal-currency">
                  {["USD", "EUR", "GBP", "CAD"].map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
              <Field label="Delivery Start *">
                <Input type="date" value={form.delivery_start} onChange={set("delivery_start")} data-testid="deal-start" />
              </Field>
              <Field label="Delivery End *">
                <Input type="date" value={form.delivery_end} onChange={set("delivery_end")} data-testid="deal-end" />
              </Field>
              <Field label="Transport Mode *">
                <Select value={form.transport_mode} onChange={set("transport_mode")} data-testid="deal-mode">
                  <MasterOptions items={byKind("transport_mode")} />
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onChange={set("status")} data-testid="deal-status">
                  {["open", "scheduled", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </Field>
            </div>
            <div className="mt-4">
              <Btn onClick={save} disabled={saving} data-testid="deal-save-btn">
                {saving ? "Saving…" : form.id ? "Update Deal" : "Create Deal"}
              </Btn>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <Th>Deal No</Th><Th>Side</Th><Th>Commodity</Th><Th>Counterparty</Th>
                <Th className="text-right">Volume</Th><Th className="text-right">Price</Th>
                <Th>Location</Th><Th>Delivery</Th><Th>Mode</Th><Th>Status</Th><Th className="w-16" />
              </tr>
            </thead>
            <tbody>
              {deals.map((d) => (
                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`deal-row-${d.deal_no}`}>
                  <td className="py-2 pr-3 font-mono text-xs font-semibold text-[#0B1E3F]">{d.deal_no}</td>
                  <td className="py-2 pr-3"><Badge v={d.side} /></td>
                  <td className="py-2 pr-3 font-semibold">{d.commodity}</td>
                  <td className="py-2 pr-3 text-xs">{d.counterparty}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtN(d.volume)} <span className="text-[10px] text-slate-400">{d.uom}</span></td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {d.price_type === "formula"
                      ? <span className="text-[11px] italic text-slate-500" title={d.price_formula}>{d.price_formula || "formula"}</span>
                      : `${fmtN(d.price, 2)} ${d.currency}`}
                  </td>
                  <td className="py-2 pr-3 text-xs">{d.location}</td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{d.delivery_start} → {d.delivery_end}</td>
                  <td className="py-2 pr-3 text-xs">{d.transport_mode}</td>
                  <td className="py-2 pr-3"><Badge v={d.status} /></td>
                  <td className="py-2">
                    <div className="flex gap-1.5">
                      <button onClick={() => setForm({ ...d })} data-testid={`deal-edit-${d.deal_no}`}
                        className="text-slate-400 hover:text-[#0B1E3F]" title="Edit"><PencilSimple size={15} /></button>
                      <button onClick={() => remove(d)} data-testid={`deal-delete-${d.deal_no}`}
                        className="text-slate-400 hover:text-red-600" title="Delete"><Trash size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {deals.length === 0 && (
                <tr><td colSpan={11} className="py-6 text-center text-slate-400 text-xs" data-testid="deals-empty">
                  No deals yet — capture your first trade with “New Deal”.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
